export const SUPPORTED_LOCALES = ["en-US", "en-AU"] as const;

export type Locale = (typeof SUPPORTED_LOCALES)[number];

export const DEFAULT_LOCALE = "en-US" satisfies Locale;

const SUPPORTED_LOCALE_SET = new Set<Locale>(SUPPORTED_LOCALES);

function isLocale(value: string): value is Locale {
  return (SUPPORTED_LOCALE_SET as ReadonlySet<string>).has(value);
}

/**
 * Normalizes a locale input to a {@link Locale} from {@link SUPPORTED_LOCALES}.
 *
 * Accepts BCP-47 dashed form (`en-US`) and the legacy underscore form (`en_US`)
 * with any casing. Anything that doesn't resolve to a supported locale falls
 * back to {@link DEFAULT_LOCALE}.
 *
 * @param input - Raw locale string from a payload, header, or HTML attribute
 * @returns A supported {@link Locale}
 */
export function normalizeLocale(input?: string | null): Locale {
  if (!input) return DEFAULT_LOCALE;

  const [language, region] = input.replace("_", "-").split("-");
  if (!language || !region) return DEFAULT_LOCALE;

  const candidate = `${language.toLowerCase()}-${region.toUpperCase()}`;
  return isLocale(candidate) ? candidate : DEFAULT_LOCALE;
}

/** ISO 4217 currency → supported {@link Locale}. */
const LOCALE_BY_CURRENCY = new Map<string, Locale>([
  ["USD", "en-US"],
  ["AUD", "en-AU"],
]);

/**
 * Resolves a supported {@link Locale} from a refund request's ISO 4217
 * currency. This is the source of truth for region-specific copy and
 * validation (e.g. AU postcode vs US ZIP) — it comes from the refund/API,
 * not the browser. Unknown or missing currencies fall back to
 * {@link DEFAULT_LOCALE}, so US stays unaffected.
 *
 * @param currency - ISO 4217 currency code, e.g. `"USD"` or `"AUD"`
 */
export function localeForCurrency(currency?: string | null): Locale {
  if (!currency) return DEFAULT_LOCALE;
  return (
    LOCALE_BY_CURRENCY.get(currency.trim().toUpperCase()) ?? DEFAULT_LOCALE
  );
}

/** Supported {@link Locale} → ISO 3166-1 alpha-2 country code. */
const COUNTRY_BY_LOCALE = new Map<Locale, string>([
  ["en-US", "US"],
  ["en-AU", "AU"],
]);

/**
 * Resolves the region's ISO 3166-1 alpha-2 country code for a supported
 * {@link Locale} (e.g. `en-US` → `"US"`, `en-AU` → `"AU"`). Used where an
 * address/profile needs a country and it must follow the refund's region
 * rather than a hardcoded default.
 */
export function countryForLocale(locale: Locale): string {
  return COUNTRY_BY_LOCALE.get(locale) ?? "US";
}
